import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  solarModulesService,
  moduleBrandsService,
  invertersService,
  inverterBrandsService,
  optimizersService,
  combinersService,
  rsdsService,
  batteriesService,
  batteryBrandsService,
  structuralOptionsService,
  mspService,
  poiService,
  poiLocationsService,
  discoService,
  conduitService,
  attachmentsService,
  railsService,
  constructionReviewsService,
  roofTypesService,
  roofConditionsService,
  reroofStatusService,
  buildingHeightsService,
  electricConfigService,
  settingsService
} from '../../services';
import {
  SolarModule,
  ModuleBrand,
  Inverter,
  InverterBrand,
  Optimizer,
  Battery,
  BatteryBrand,
  Structural,
  StructuralCategory,
  Msp,
  Poi,
  PoiLocation,
  AtsBreakerSize,
  Disco,
  Conduit,
  Combiner,
  Rsd,
  Attachment,
  Rail,
  ConstructionReview,
  RoofType,
  RoofCondition,
  ReroofStatus,
  BuildingHeight,
  ElectricConfig,
  Setting
} from '../../types';

interface DatabaseState {
  db_data: {
    // Module related
    solar_modules: SolarModule[];
    module_brands: ModuleBrand[];
    
    // Inverter related
    inverters: Inverter[];
    inverter_brands: InverterBrand[];
    optimizers: Optimizer[];
    combiners: Combiner[];
    rsds: Rsd[];

    // Battery related
    batteries: Battery[];
    battery_brands: BatteryBrand[];
    
    // Structural
    structural: {
      framing_sizes: Structural[];
      framing_types: Structural[];
      rafter_spacings: Structural[];
      mount_systems: Structural[];
    };
    
    // MSP
    msp: {
      busses: Msp[];
      breakers: Msp[];
      manufacturers: Msp[];
      locations: Msp[];
      styles: Msp[];
      types: Msp[];
      cad_locations: Msp[];
    };
    
    // Other equipment
    poi: Poi[];
    poi_locations: PoiLocation[];
    ats_breaker_sizes: AtsBreakerSize[];
    disco: Disco[];
    conduit: {
      sizes: Conduit[];
      types: Conduit[];
    };
    attachments: Attachment[];
    rails: Rail[];

    // Building Attributes
    roof_types: RoofType[];
    roof_conditions: RoofCondition[];
    reroof_statuses: ReroofStatus[];
    building_heights: BuildingHeight[];

    // Electric Configurations
    electric_configs: ElectricConfig[];

    // Settings
    settings: Setting[];
  };
  
  // Construction Reviews (separate from db_data as they're user-generated)
  db_crs: ConstructionReview[];
  db_crs_pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  
  // Loading states
  loading: {
    db_data: boolean;
    db_crs: boolean;
  };
  
  // Error states
  errors: {
    db_data?: string;
    db_crs?: string;
  };
  
  // Track if initial load is complete
  is_initialized: boolean;
}

const initialState: DatabaseState = {
  db_data: {
    solar_modules: [],
    module_brands: [],
    inverters: [],
    inverter_brands: [],
    optimizers: [],
    combiners: [],
    rsds: [],
    batteries: [],
    battery_brands: [],
    structural: {
      framing_sizes: [],
      framing_types: [],
      rafter_spacings: [],
      mount_systems: []
    },
    msp: {
      busses: [],
      breakers: [],
      manufacturers: [],
      locations: [],
      styles: [],
      types: [],
      cad_locations: []
    },
    poi: [],
    poi_locations: [],
    ats_breaker_sizes: [],
    disco: [],
    conduit: {
      sizes: [],
      types: []
    },
    attachments: [],
    rails: [],

    // Building Attributes
    roof_types: [],
    roof_conditions: [],
    reroof_statuses: [],
    building_heights: [],

    // Electric Configurations
    electric_configs: [],

    // Settings
    settings: []
  },
  db_crs: [],
  db_crs_pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
  loading: {
    db_data: false,
    db_crs: false
  },
  errors: {},
  is_initialized: false
};

// Async thunks for fetching data
export const fetchAllDatabaseData = createAsyncThunk(
  'database/fetchDbData',
  async () => {
    const [
      solar_modules,
      module_brands,
      inverters,
      inverter_brands,
      optimizers,
      combiners,
      rsds,
      batteries,
      battery_brands,
      all_structural_data,
      all_msp_data,
      poi,
      poi_locations,
      ats_breaker_sizes,
      disco,
      all_conduit_data,
      attachments,
      rails,
      roof_types,
      roof_conditions,
      reroof_statuses,
      building_heights,
      electric_configs,
      settings
    ] = await Promise.all([
      solarModulesService.getAllModules(),
      moduleBrandsService.getAllBrands(),
      invertersService.getAllInverters(),
      inverterBrandsService.getAllBrands(),
      optimizersService.getAllOptimizers(),
      combinersService.getAll(),
      rsdsService.getAll(),
      batteriesService.getAllBatteries(),
      batteryBrandsService.getAllBatteryBrands(),
      structuralOptionsService.getAllStructuralOptions(),
      mspService.getAllMsp(),
      poiService.getAllPoi(),
      poiLocationsService.getAll(),
      poiLocationsService.getAllAtsBreakerSizes(),
      discoService.getAllDisco(),
      conduitService.getAllConduit(),
      attachmentsService.getAllAttachments(),
      railsService.getAllRails(),
      roofTypesService.getAllRoofTypes(),
      roofConditionsService.getAllRoofConditions(),
      reroofStatusService.getAllReroofStatuses(),
      buildingHeightsService.getAllBuildingHeights(),
      electricConfigService.getAllElectricConfigs(),
      settingsService.getAll()
    ]);

    // Map Structural data by category
    const framing_sizes = all_structural_data.filter(item => item.category === StructuralCategory.FRAMING_SIZE);
    const framing_types = all_structural_data.filter(item => item.category === StructuralCategory.FRAMING_TYPE);
    const rafter_spacings = all_structural_data.filter(item => item.category === StructuralCategory.RAFTER_SPACING);
    const mount_systems = all_structural_data.filter(item => item.category === StructuralCategory.MOUNT_SYSTEM);

    // Map MSP data by category
    const msp_busses = all_msp_data.filter(item => item.category === 'msp_buss');
    const msp_breakers = all_msp_data.filter(item => item.category === 'msp_breaker');
    const msp_manufacturers = all_msp_data.filter(item => item.category === 'msp_manufacturer');
    const msp_locations = all_msp_data.filter(item => item.category === 'msp_location');
    const msp_styles = all_msp_data.filter(item => item.category === 'msp_style');
    const msp_types = all_msp_data.filter(item => item.category === 'msp_type');
    const msp_cad_locations = all_msp_data.filter(item => item.category === 'msp_cad_location');

    // Map Conduit data by category
    const conduit_sizes = all_conduit_data.filter(item => item.category === 'size');
    const conduit_types = all_conduit_data.filter(item => item.category === 'type');

    return {
      solar_modules,
      module_brands,
      inverters,
      inverter_brands,
      optimizers,
      combiners,
      rsds,
      batteries,
      battery_brands,
      framing_sizes,
      framing_types,
      rafter_spacings,
      mount_systems,
      msp_busses,
      msp_breakers,
      msp_manufacturers,
      msp_locations,
      msp_styles,
      msp_types,
      msp_cad_locations,
      poi,
      poi_locations,
      ats_breaker_sizes,
      disco,
      conduit_sizes,
      conduit_types,
      attachments,
      rails,
      roof_types,
      roof_conditions,
      reroof_statuses,
      building_heights,
      electric_configs,
      settings
    };
  }
);

export const fetchConstructionReviews = createAsyncThunk(
  'database/fetchDbCrs',
  async (params?: { page?: number; limit?: number; search?: string; sortBy?: 'created_at' | 'updated_at' | 'task_name'; sortOrder?: 'asc' | 'desc' }) => {
    return await constructionReviewsService.getAll(params);
  }
);

const databaseSlice = createSlice({
  name: 'database',
  initialState,
  reducers: {
    // Clear all errors
    clearErrors: (state) => {
      state.errors = {};
    },
    // Module Brand actions
    addModuleBrand: (state, action: PayloadAction<ModuleBrand>) => {
      state.db_data.module_brands.push(action.payload);
    },
    // Solar Module actions
    addSolarModule: (state, action: PayloadAction<SolarModule>) => {
      state.db_data.solar_modules.unshift(action.payload);
    },
    // Inverter Brand actions
    addInverterBrand: (state, action: PayloadAction<InverterBrand>) => {
      state.db_data.inverter_brands.push(action.payload);
    },
    // Inverter actions
    addInverter: (state, action: PayloadAction<Inverter>) => {
      state.db_data.inverters.push(action.payload);
    },
    // Optimizer actions
    addOptimizer: (state, action: PayloadAction<Optimizer>) => {
      state.db_data.optimizers.push(action.payload);
    },
    // Combiner actions
    addCombiner: (state, action: PayloadAction<Combiner>) => {
      state.db_data.combiners.push(action.payload);
    },
    // RSD actions
    addRsd: (state, action: PayloadAction<Rsd>) => {
      state.db_data.rsds.push(action.payload);
    },
    // Battery Brand actions
    addBatteryBrand: (state, action: PayloadAction<BatteryBrand>) => {
      state.db_data.battery_brands.push(action.payload);
    },
    // Battery actions
    addBattery: (state, action: PayloadAction<Battery>) => {
      state.db_data.batteries.unshift(action.payload);
    },
    // MSP actions
    addMspBuss: (state, action: PayloadAction<Msp>) => {
      state.db_data.msp.busses.push(action.payload);
    },
    addMspBreaker: (state, action: PayloadAction<Msp>) => {
      state.db_data.msp.breakers.push(action.payload);
    },
    addMspManufacturer: (state, action: PayloadAction<Msp>) => {
      state.db_data.msp.manufacturers.push(action.payload);
    },
    addMspLocation: (state, action: PayloadAction<Msp>) => {
      state.db_data.msp.locations.push(action.payload);
    },
    addMspStyle: (state, action: PayloadAction<Msp>) => {
      state.db_data.msp.styles.push(action.payload);
    },
    addMspType: (state, action: PayloadAction<Msp>) => {
      state.db_data.msp.types.push(action.payload);
    },
    addMspCadLocation: (state, action: PayloadAction<Msp>) => {
      state.db_data.msp.cad_locations.push(action.payload);
    },
    // POI actions
    addPoi: (state, action: PayloadAction<Poi>) => {
      state.db_data.poi.push(action.payload);
    },
    addPoiLocation: (state, action: PayloadAction<PoiLocation>) => {
      state.db_data.poi_locations.push(action.payload);
    },
    addAtsBreakerSize: (state, action: PayloadAction<AtsBreakerSize>) => {
      state.db_data.ats_breaker_sizes.push(action.payload);
    },
    // DISCO actions
    addDisco: (state, action: PayloadAction<Disco>) => {
      state.db_data.disco.unshift(action.payload);
    },
    // Conduit actions
    addConduitSize: (state, action: PayloadAction<Conduit>) => {
      state.db_data.conduit.sizes.push(action.payload);
    },
    addConduitType: (state, action: PayloadAction<Conduit>) => {
      state.db_data.conduit.types.push(action.payload);
    },
    // Structural actions
    addStructuralFramingSize: (state, action: PayloadAction<Structural>) => {
      state.db_data.structural.framing_sizes.push(action.payload);
    },
    addStructuralFramingType: (state, action: PayloadAction<Structural>) => {
      state.db_data.structural.framing_types.push(action.payload);
    },
    addStructuralRafterSpacing: (state, action: PayloadAction<Structural>) => {
      state.db_data.structural.rafter_spacings.push(action.payload);
    },
    addStructuralMountSystem: (state, action: PayloadAction<Structural>) => {
      state.db_data.structural.mount_systems.push(action.payload);
    },
    // Attachment actions
    addAttachment: (state, action: PayloadAction<Attachment>) => {
      state.db_data.attachments.push(action.payload);
    },
    // Rail actions
    addRail: (state, action: PayloadAction<Rail>) => {
      state.db_data.rails.push(action.payload);
    },
    // Roof Type actions
    addRoofType: (state, action: PayloadAction<RoofType>) => {
      state.db_data.roof_types.push(action.payload);
    },
    // Roof Condition actions
    addRoofCondition: (state, action: PayloadAction<RoofCondition>) => {
      state.db_data.roof_conditions.push(action.payload);
    },
    // Reroof Status actions
    addReroofStatus: (state, action: PayloadAction<ReroofStatus>) => {
      state.db_data.reroof_statuses.push(action.payload);
    },
    // Building Height actions
    addBuildingHeight: (state, action: PayloadAction<BuildingHeight>) => {
      state.db_data.building_heights.push(action.payload);
    },
    // Electric Config actions
    addElectricConfig: (state, action: PayloadAction<ElectricConfig>) => {
      state.db_data.electric_configs.unshift(action.payload);
    },
    // Settings actions
    updateSetting: (state, action: PayloadAction<Setting>) => {
      const index = state.db_data.settings.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.db_data.settings[index] = action.payload;
      }
    },
    // Construction Review actions
    updateConstructionReview: (state, action: PayloadAction<ConstructionReview>) => {
      const index = state.db_crs.findIndex(cr => cr.id === action.payload.id);
      if (index !== -1) {
        state.db_crs[index] = action.payload;
      }
    },
    addConstructionReview: (state, action: PayloadAction<ConstructionReview>) => {
      state.db_crs.push(action.payload);
    },
    deleteConstructionReview: (state, action: PayloadAction<string>) => {
      state.db_crs = state.db_crs.filter(cr => cr.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all database data
      .addCase(fetchAllDatabaseData.pending, (state) => {
        state.loading.db_data = true;
        state.errors.db_data = undefined;
      })
      .addCase(fetchAllDatabaseData.fulfilled, (state, action) => {
        // Update db_data with all fetched data
        state.db_data = {
          solar_modules: action.payload.solar_modules,
          module_brands: action.payload.module_brands,
          inverters: action.payload.inverters,
          inverter_brands: action.payload.inverter_brands,
          optimizers: action.payload.optimizers,
          combiners: action.payload.combiners,
          rsds: action.payload.rsds,
          batteries: action.payload.batteries,
          battery_brands: action.payload.battery_brands,
          structural: {
            framing_sizes: action.payload.framing_sizes,
            framing_types: action.payload.framing_types,
            rafter_spacings: action.payload.rafter_spacings,
            mount_systems: action.payload.mount_systems,
          },
          msp: {
            busses: action.payload.msp_busses,
            breakers: action.payload.msp_breakers,
            manufacturers: action.payload.msp_manufacturers,
            locations: action.payload.msp_locations,
            styles: action.payload.msp_styles,
            types: action.payload.msp_types,
            cad_locations: action.payload.msp_cad_locations,
          },
          poi: action.payload.poi,
          poi_locations: action.payload.poi_locations,
          ats_breaker_sizes: action.payload.ats_breaker_sizes,
          disco: action.payload.disco,
          conduit: {
            sizes: action.payload.conduit_sizes,
            types: action.payload.conduit_types,
          },
          attachments: action.payload.attachments,
          rails: action.payload.rails,

          // Building Attributes
          roof_types: action.payload.roof_types,
          roof_conditions: action.payload.roof_conditions,
          reroof_statuses: action.payload.reroof_statuses,
          building_heights: action.payload.building_heights,

          // Electric Configurations
          electric_configs: action.payload.electric_configs,

          // Settings
          settings: action.payload.settings
        };
        
        state.loading.db_data = false;
        state.is_initialized = true;
      })
      .addCase(fetchAllDatabaseData.rejected, (state, action) => {
        state.loading.db_data = false;
        state.errors.db_data = action.error.message;
        state.is_initialized = true;
      })
      
      // Fetch Construction Reviews
      .addCase(fetchConstructionReviews.pending, (state) => {
        state.loading.db_crs = true;
        state.errors.db_crs = undefined;
      })
      .addCase(fetchConstructionReviews.fulfilled, (state, action) => {
        state.db_crs = action.payload.data;
        state.db_crs_pagination = {
          total: action.payload.total,
          page: action.payload.page,
          limit: action.payload.limit,
          totalPages: action.payload.totalPages,
        };
        state.loading.db_crs = false;
      })
      .addCase(fetchConstructionReviews.rejected, (state, action) => {
        state.loading.db_crs = false;
        state.errors.db_crs = action.error.message;
      });
  },
});

export const { clearErrors, addModuleBrand, addSolarModule, addInverterBrand, addInverter, addOptimizer, addCombiner, addRsd, addBatteryBrand, addBattery, addMspBuss, addMspBreaker, addMspManufacturer, addMspLocation, addMspStyle, addMspType, addMspCadLocation, addPoi, addPoiLocation, addAtsBreakerSize, addDisco, addConduitSize, addConduitType, addStructuralFramingSize, addStructuralFramingType, addStructuralRafterSpacing, addStructuralMountSystem, addAttachment, addRail, addRoofType, addRoofCondition, addReroofStatus, addBuildingHeight, addElectricConfig, updateSetting, updateConstructionReview, addConstructionReview, deleteConstructionReview } = databaseSlice.actions;
export default databaseSlice.reducer;